import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { getFileDownload } from '@cp/src/utils/commonUtils';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import {
  setViewReportStatusData,
  setViewReportStatusLoading
} from '@cp/src/reducers/TeamLeadSlice';
import {
  setConfigureReportsData,
  setConfigureReportsLoading,
  setColumnConfigurationData,
  setColumnConfigurationLoading,
  clearColumnConfigurationData
} from '@cp/src/reducers/configureReportsSlice';
import { Dispatch } from '@reduxjs/toolkit';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';

export const getViewReportStatus = () => (dispatch: Dispatch) => {
  dispatch(setViewReportStatusLoading(true));
  const url = getApiUrl(ApiKeys.VIEW_REPORT_STATUS);
  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      dispatch(setViewReportStatusData(res?.data));
    })
    .catch(error => {
      logError(error);
      toast.error('Failed to fetch report status');
    })
    .finally(() => {
      dispatch(setViewReportStatusLoading(false));
    });
};

export const handleReportDownload = (reportType: string) => {
  const url = getApiUrl(ApiKeys.GENERATE_REPORT, { reportType });
  axiosInstance
    .post(url, {}, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
    .then(response => {
      if (response?.data) {
        const fileUrl = response?.data?.data;
        getFileDownload(fileUrl, true);
        toast.success('You will receive an email shortly!');
      }
      if (response.status === API_STATUS_CODE.OK) {
        toast.success('You will receive an email shortly!');
      }
    })
    .catch(error => {
      logError(error);
      const errorMessage = error?.response?.data?.message || 'Email not sent';
      toast.error(errorMessage);
    });
};

export const handleDownloadButtonClick = (reportType: string, requestId: string) => {
  const url = getApiUrl(ApiKeys.DOWNLOAD_REPORT_STATUS, { reportType, requestId });
  axiosInstance
    .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
    .then(response => {
      if (response?.data) {
        const fileUrl = response?.data;
        getFileDownload(fileUrl, true);
        toast.success('Report downloaded successfully');
      }
    })
    .catch(error => {
      logError(error);
      toast.error('Failed to download report');
    });
};

export const getConfigureReports = () => (dispatch: Dispatch) => {
  dispatch(setConfigureReportsLoading(true));
  const url = getApiUrl(ApiKeys.GET_CONFIGURE_REPORTS);
  axiosInstance
    .get(url, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
    .then(res => {
      dispatch(setConfigureReportsData(res?.data));
    })
    .catch(error => {
      logError(error);
      toast.error('Failed to fetch configure reports');
    })
    .finally(() => {
      dispatch(setConfigureReportsLoading(false));
    });
};

export const getColumnConfiguration =
  (reportType: string, teamType: string) => (dispatch: Dispatch) => {
    dispatch(setColumnConfigurationLoading(true));
    const url = getApiUrl(ApiKeys.GET_COLUMN_CONFIGURATION);

    axiosInstance
      .get(url, {
        params: { reportType, teamType },
        headers: { donotHandleError: true, includeInUptimeMetrics: true }
      })
      .then(res => {
        const columnData = res?.data;
        dispatch(setColumnConfigurationData(columnData));
      })
      .catch(error => {
        logError(error);
        toast.error('Failed to fetch column configuration');
        dispatch(clearColumnConfigurationData());
      })
      .finally(() => {
        dispatch(setColumnConfigurationLoading(false));
      });
  };

export const postColumnConfiguration =
  (reportType: string, teamType: string, selectedColumns: string[]) => (dispatch: Dispatch) => {
    dispatch(setColumnConfigurationLoading(true));
    const url = getApiUrl(ApiKeys.GET_COLUMN_CONFIGURATION);

    const payload = {
      baseReportType: reportType,
      teamType,
      columns: selectedColumns
    };

    axiosInstance
      .put(url, payload, { headers: { donotHandleError: true, includeInUptimeMetrics: true } })
      .then(() => {
        toast.success('Configuration saved successfully');
        dispatch(getConfigureReports());
      })
      .catch(error => {
        logError(error);
        const errorMessage =
          error?.response?.data?.message || 'Failed to save column configuration';
        toast.error(errorMessage);
      })
      .finally(() => {
        dispatch(setColumnConfigurationLoading(false));
      });
  };
